import {
  AsbestosDetection,
  AsbestosFinding,
  AsbestosParameterKind,
  asbestosPmIds,
  getTypedValFromEnum,
  getTypedValFromEnumWithCategory,
  isPercentage,
  kmfPmIds,
} from '@ivario-arc/shared/src/sampleResults/asbestos.ts';
import { ConfigDto, SampleDetailsDto } from '@src/types/types.ts';
import { AsbestosTargetType } from '@src/asbestos/types.ts';

export const categorizedParameters = (sample: SampleDetailsDto) => {
  const asbestosValues = [
    ...sample.parameters.filter(
      (x) =>
        !!getTypedValFromEnumWithCategory(
          x.pmId,
          x.value,
          AsbestosParameterKind.ASBESTOS,
        ),
    ),
  ]
    .sort((a, b) => parseInt(a.pmId) - parseInt(b.pmId))
    .map((parameter) => ({
      rawParameter: parameter,
      detection: getTypedValFromEnumWithCategory(
        parameter.pmId,
        parameter.value,
        AsbestosParameterKind.ASBESTOS,
      ),
    }));

  const kmfValues = sample.parameters
    .filter(
      (x) =>
        !!getTypedValFromEnumWithCategory(
          x.pmId,
          x.value,
          AsbestosParameterKind.KMF,
        ),
    )
    .sort((a, b) => parseInt(a.pmId) - parseInt(b.pmId))
    .map((parameter) => ({
      rawParameter: parameter,
      detection: getTypedValFromEnumWithCategory(
        parameter.pmId,
        parameter.value,
        AsbestosParameterKind.KMF,
      ),
    }));

  return {
    kmfParam: kmfValues.length > 0 ? kmfValues : [],
    asbestosParam: asbestosValues.length > 0 ? asbestosValues : [],
  };
};

export const hasInfoToShow = (sample: SampleDetailsDto) => {
  const parameters = categorizedParameters(sample);

  return (
    parameters.kmfParam.some((param) => !!param.detection?.finding) ||
    parameters.asbestosParam.some((param) => !!param.detection?.finding)
  );
};

const remAsbestosImageToken = /_b1_/;
const remKmfImageToken = /_b2_/;
const edxAsbestosImageToken = /_s1_/;
const edxKmfImageToken = /_s2_/;
const asbestosImageToken = /_(b|s)1_/;
const kmfImageToken = /_(b|s)2_/;

export const getImages = (
  sample: SampleDetailsDto,
  targetType: AsbestosTargetType,
) => {
  return sample.images.filter((image) =>
    (targetType === AsbestosTargetType.ASB
      ? asbestosImageToken
      : kmfImageToken
    ).test(image.url),
  );
};

export const getAsbestosImageCaption = (
  url: string,
  configuration: ConfigDto,
  targetType: AsbestosTargetType,
) => {
  if (targetType === AsbestosTargetType.ASB) {
    return remAsbestosImageToken.test(url)
      ? configuration.asbestosRemImageClarification
      : edxAsbestosImageToken.test(url)
        ? configuration.asbestosEdxImageClarification
        : '';
  }

  return remKmfImageToken.test(url)
    ? configuration.kmfRemImageClarification
    : edxKmfImageToken.test(url)
      ? configuration.kmfEdxImageClarification
      : '';
};

export const getEffectiveRegulation = (
  sample: SampleDetailsDto,
  targetType: AsbestosTargetType,
) => {
  const parameterToShowInfo =
    targetType === AsbestosTargetType.ASB
      ? categorizedParameters(sample).asbestosParam
      : categorizedParameters(sample).kmfParam;

  if (parameterToShowInfo.length === 0) {
    return null;
  }

  const parameterFinding =
    parameterToShowInfo[0].detection?.finding ?? AsbestosDetection.NONE;
  const targetRegulationValue =
    parameterFinding === AsbestosDetection.NONE ? false : true;
  const isTargetRegulationKmfNonWho =
    targetType === AsbestosTargetType.KMF &&
    parameterFinding === AsbestosDetection.KMF_NON_WHO
      ? true
      : targetType !== AsbestosTargetType.ASB
        ? false
        : null;
  const isTalkDetected =
    targetType === AsbestosTargetType.ASB &&
    parameterFinding === AsbestosDetection.TALK;

  const regulations = parameterToShowInfo[0].rawParameter.regulations.filter(
    (reg) => {
      if (targetType === AsbestosTargetType.ASB) {
        if (isTalkDetected) {
          return !!reg.isTalk;
        }
        return !!reg.yesNo === targetRegulationValue && !reg.isTalk;
      }
      return isTargetRegulationKmfNonWho
        ? !!reg.isKmfNonWho
        : !!reg.yesNo === targetRegulationValue && !reg.isKmfNonWho;
    },
  );

  return regulations;
};

export const areAllAnalysable = (sample: SampleDetailsDto) => {
  return (
    sample.parameters.length > 0 &&
    sample.parameters
      .filter((parameter) => parameter.format !== 'percentage')
      .every((x) => !x.notAnalyzable)
  );
};

export const getDetection = (
  targetType: AsbestosTargetType,
  sample: SampleDetailsDto,
): {
  val: string;
  detection: AsbestosDetection;
  pmId: string | undefined;
} => {
  const findings = sample.parameters
    .filter((x) => !isPercentage(x.pmId))
    .filter((x) =>
      (targetType === AsbestosTargetType.ASB
        ? asbestosPmIds
        : kmfPmIds
      ).includes(parseInt(x.pmId)),
    )
    .map((x) => {
      if (!x.value) {
        return undefined;
      }

      return {
        typedVal: getTypedValFromEnum(
          x.pmId,
          AsbestosFinding[x.value as keyof typeof AsbestosFinding],
        ),
        pmId: x.pmId,
      };
    })
    .filter((x) => x?.typedVal);

  if (targetType === AsbestosTargetType.ASB) {
    const asbestosFinding = findings.filter((x) =>
      [
        AsbestosDetection.ASBESTOS,
        AsbestosDetection.TALK,
        AsbestosDetection.NONE,
      ].includes(x?.typedVal?.finding ?? AsbestosDetection.NONE),
    );

    return {
      val: asbestosFinding[0]?.typedVal?.val ?? '',
      detection:
        asbestosFinding[0]?.typedVal?.finding ?? AsbestosDetection.NONE,
      pmId: asbestosFinding[0]?.pmId,
    };
  }

  const kmfFinding = findings.filter(
    (x) =>
      x?.typedVal?.finding === AsbestosDetection.KMF ||
      x?.typedVal?.finding === AsbestosDetection.KMF_NON_WHO ||
      x?.typedVal?.finding === AsbestosDetection.NONE,
  );
  return {
    val: kmfFinding[0]?.typedVal?.val ?? '',
    detection: kmfFinding[0]?.typedVal?.finding ?? AsbestosDetection.NONE,
    pmId: kmfFinding[0]?.pmId,
  };
};

export const getPercentageParameter = (sample: SampleDetailsDto) => {
  const parameters = sample.parameters.filter(
    (p) =>
      !!getTypedValFromEnumWithCategory(
        p.pmId,
        p.value,
        AsbestosParameterKind.ASBESTOS_PERCENTAGE,
      ),
  );

  if (parameters.length === 0) {
    return null;
  }
  return parameters[0];
};
